import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import BracketButton from './BracketButton';
import { HERO_VIDEO, company } from '../mock/mock';

const Hero = () => {
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const play = async () => {
      try {
        v.playbackRate = 0.5;
        await v.play();
        setVideoReady(true);
      } catch (e) {
        setVideoReady(false);
      }
    };
    play();
  }, []);

  return (
    <section className="relative w-full h-screen min-h-[720px] overflow-hidden bg-black text-white">
      {/* Background video */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        src={HERO_VIDEO}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
      />
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/55" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/80" />
      <div className="absolute inset-0 grid-backdrop--dark opacity-25" />

      {/* Ticker bar */}
      <div className="absolute top-20 left-0 right-0 border-t border-b border-white/15 bg-black/20 backdrop-blur-sm overflow-hidden">
        <div className="marquee-track py-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center">
              {['Flatbed', 'Hotshot', 'Heavy Haul', 'Oilfield', 'Logistics', 'Nationwide 48 States', 'DOT #3841902', 'MC 1207834'].map((t, j) => (
                <span key={`${i}-${j}`} className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/70 px-8 whitespace-nowrap">
                  {t} <span className="text-white/30 mx-1">•</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-end pb-24 lg:pb-32">
        <div className="max-w-[1400px] w-full mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="fade-up">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-[1.5px] bg-white/70" />
                  <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-white/80">
                    Headquartered in San Antonio, Texas
                  </span>
                </div>
                <h1 className="big-display text-white text-[64px] leading-[0.9] sm:text-[96px] lg:text-[132px] xl:text-[152px]">
                  Heavy Haul.
                  <br />
                  <span className="block">
                    Hotshot. <span className="text-white/40">Handled.</span>
                  </span>
                </h1>
              </div>
            </div>
            <div className="lg:col-span-4 lg:pb-3">
              <p className="font-body text-white/85 text-base lg:text-[17px] leading-relaxed max-w-md fade-up" style={{ animationDelay: '0.2s' }}>
                Caprock Logistics Group specializes in flatbed haul, oilfield hotshot, heavy machinery and full-load freight — dispatched 24/7 out of San Antonio, Texas.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-8 fade-up" style={{ animationDelay: '0.35s' }}>
                <BracketButton to="/contact" variant="light">Get A Quote</BracketButton>
                <BracketButton to="/services" variant="light">Our Services</BracketButton>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/60 z-10">
        <span className="font-mono-tech text-[9px] tracking-[0.3em] uppercase">Scroll</span>
        <ChevronDown className="w-4 h-4 animate-bounce" />
      </div>

      {!videoReady && (
        <div className="absolute inset-0 bg-neutral-900" />
      )}
    </section>
  );
};

export default Hero;
